import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Clock } from "lucide-react";
import { type ThemePreference } from "@/hooks/useTheme";

interface ThemeOnboardingProps {
  onSelect: (theme: ThemePreference) => void;
}

const themes = [
  {
    id: "classic" as ThemePreference,
    label: "Classico",
    description: "Tema chiaro e luminoso",
    icon: Sun,
    gradient: "from-amber-400 to-orange-500",
    bg: "bg-white",
    preview: "bg-gradient-to-br from-gray-50 to-gray-100",
  },
  {
    id: "dark" as ThemePreference,
    label: "Dark Mode",
    description: "Tema scuro ed elegante",
    icon: Moon,
    gradient: "from-indigo-500 to-purple-600",
    bg: "bg-gray-900",
    preview: "bg-gradient-to-br from-gray-900 to-gray-800",
  },
  {
    id: "auto" as ThemePreference,
    label: "Automatico",
    description: "Si adatta all'orario",
    icon: Clock,
    gradient: "from-blue-400 to-cyan-500",
    bg: "bg-gradient-to-r from-white to-gray-900",
    preview: "bg-gradient-to-r from-gray-100 via-blue-100 to-gray-800",
  },
];

export const ThemeOnboarding = ({ onSelect }: ThemeOnboardingProps) => {
  const [selected, setSelected] = useState<ThemePreference | null>(null);
  const [exiting, setExiting] = useState(false);

  const handleConfirm = () => {
    if (!selected) return;
    setExiting(true);
    setTimeout(() => onSelect(selected), 500);
  };

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 backdrop-blur-xl px-6"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-center mb-10"
          >
            <h1 className="text-3xl font-bold text-white mb-2">Benvenuto su Tecly</h1>
            <p className="text-white/60 text-base">Scegli il tema che preferisci</p>
          </motion.div>

          <div className="flex flex-col gap-4 w-full max-w-sm">
            {themes.map((theme, i) => {
              const Icon = theme.icon;
              const isSelected = selected === theme.id;
              return (
                <motion.button
                  key={theme.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
                  onClick={() => setSelected(theme.id)}
                  className={`relative flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-300 ${
                    isSelected
                      ? "border-white/80 bg-white/15 scale-[1.03]"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold text-lg">{theme.label}</p>
                    <p className="text-white/50 text-sm">{theme.description}</p>
                  </div>
                  {isSelected && (
                    <motion.div
                      layoutId="check"
                      className="absolute right-4 w-6 h-6 rounded-full bg-white flex items-center justify-center"
                    >
                      <div className="w-3 h-3 rounded-full bg-black" />
                    </motion.div>
                  )}
                </motion.button>
              );
            })}
          </div>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: selected ? 1 : 0.3, y: 0 }}
            transition={{ delay: 0.7, duration: 0.4 }}
            disabled={!selected}
            onClick={handleConfirm}
            className="mt-10 px-10 py-3.5 rounded-full bg-white text-black font-semibold text-lg transition-all duration-300 hover:scale-105 active:scale-95 disabled:cursor-not-allowed"
          >
            Continua
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
